import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Wallet, CreditCard, Banknote, ArrowRightLeft, Coins, Smartphone, 
  LogOut, Menu, X, TrendingUp, Activity, CheckCircle, Shield, UserCheck, MapPin, AlertCircle, Zap, Users, PlusCircle, Send 
} from 'lucide-react';
import { QRWalletCode, QRScanner } from '@/components/QRWallet';
import Insurance from '@/components/Insurance';
import LocationManager from '@/components/LocationManager';
import NfcTransfer from '@/components/NfcTransfer';
import PhonebookPicker from '@/components/PhonebookPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('mojo_token');
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');

  const refreshUserData = () => {
    // User data is stored in localStorage, so we can just trigger a re-render
    // by refreshing wallet data which will cause the component to re-read from localStorage
    fetchWalletData();
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchWalletData();
    fetchTransactions();
  }, [token, navigate]);

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data);
    } catch (error) {
      toast.error('Failed to fetch wallet data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/wallet/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const menuItems = [
    { path: '/merchant', icon: <Activity />, label: 'Dashboard' },
    { path: '/merchant/nfc-transfer', icon: <Zap />, label: 'NFC Transfer' },
    { path: '/merchant/postbank', icon: <Banknote />, label: 'Postbank' },
    { path: '/merchant/money-send', icon: <ArrowRightLeft />, label: 'Money Send' },
    { path: '/merchant/card-payment', icon: <CreditCard />, label: 'Card Payment' },
    { path: '/merchant/airtime-data', icon: <Smartphone />, label: 'Airtime & Data' },
    { path: '/merchant/funeral-policies', icon: <Shield />, label: 'Funeral Policies' },
    { path: '/merchant/insurance', icon: <Shield />, label: 'Insurance Premium' },
    { path: '/merchant/profile', icon: <UserCheck />, label: 'Profile & Settings' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F2B10C] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#1B0C34]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]" data-testid="merchant-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2B10C]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <img src="/sticker.svg" alt="Mojo Money" className="h-10 w-10" />
            <div>
              <h1 className="text-xl font-bold text-[#1B0C34]">Mojo Money</h1>
              <p className="text-xs text-[#145539]">Merchant Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-[#1B0C34]">{user.full_name}</p>
              <p className="text-xs text-[#1B0C34]/60">{user.email}</p>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-[#F2B10C]"
            >
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 flex gap-6">
        {/* Sidebar */}
        <aside className={`
          ${menuOpen ? 'block' : 'hidden'} lg:block
          fixed lg:relative inset-0 lg:inset-auto
          bg-white lg:bg-transparent
          w-64 lg:w-64 shrink-0
          z-40 lg:z-auto
          p-4 lg:p-0
        `}>
          <Card className="bg-white border-[#F2B10C]/20">
            <CardHeader>
              <CardTitle className="text-[#1B0C34]">Menu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {menuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#F2B10C]/10 transition-colors text-[#1B0C34]"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<DashboardHome wallet={wallet} transactions={transactions} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/nfc-transfer" element={<NfcTransferPage refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/postbank" element={<PostbankServices refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/money-send" element={<MoneySendServices refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/location" element={<LocationManager refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/topup" element={<WalletTopUp refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/payout" element={<CardPayout wallet={wallet} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/customer-verification" element={<CustomerIDVerification refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/card-payment" element={<CardPayment refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/wallet-transfer" element={<WalletTransfer refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/ace-cross-border" element={<ACECrossBorderTransfer refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/money-transfer" element={<MoneyTransfer refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/ace-token" element={<ACEToken wallet={wallet} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/airtime-data" element={<AirtimeData refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/funeral-policies" element={<Insurance />} />
            <Route path="/insurance" element={<InsurancePremium refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/profile" element={<ProfileSettings user={user} onUpdate={refreshUserData} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// NFC Transfer Page Component
const NfcTransferPage = ({ refreshData }) => {
  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <h2 className="text-2xl font-bold text-[#1B0C34] mb-6">NFC Wallet Transfer</h2>
      <NfcTransfer userType="merchant" />
    </div>
  );
};

// Profile Settings Component
const ProfileSettings = ({ user, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    business_name: user?.business_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    province: user?.province || '',
    id_number: user?.id_number || '',
  });

  const API = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('mojo_token');

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/users/profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profile updated successfully!');
      setEditing(false);
      
      // Update localStorage with new user data
      const currentUser = JSON.parse(localStorage.getItem('mojo_user') || '{}');
      const updatedUser = { ...currentUser, ...formData };
      localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
      
      if (onUpdate) onUpdate();
      
      // Force a page refresh to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-[#1B0C34] flex items-center gap-2">
                <UserCheck className="w-6 h-6" />
                Profile & Settings
              </CardTitle>
              <CardDescription>Manage your account details and business information</CardDescription>
            </div>
            {!editing ? (
              <Button onClick={() => setEditing(true)} className="bg-[#F2B10C] text-[#1B0C34] hover:bg-[#d99a0a]">
                Edit Profile
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  setEditing(false);
                  setFormData({
                    full_name: user?.full_name || '',
                    business_name: user?.business_name || '',
                    email: user?.email || '',
                    phone_number: user?.phone_number || '',
                    province: user?.province || '',
                    id_number: user?.id_number || '',
                  });
                }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#145539] hover:bg-[#0d3b26]">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-[#1B0C34] mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>
              <div>
                <Label>ID Number</Label>
                <Input
                  value={formData.id_number}
                  disabled={true}
                  className="bg-gray-100"
                  title="ID number cannot be changed"
                />
                <p className="text-xs text-gray-500 mt-1">ID number cannot be changed</p>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div>
            <h3 className="text-lg font-semibold text-[#1B0C34] mb-4">Business Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Business Name</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>
              <div>
                <Label>Province</Label>
                <select
                  value={formData.province}
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                  disabled={!editing}
                  className={`w-full px-3 py-2 border rounded-md ${!editing ? 'bg-gray-50' : ''}`}
                >
                  <option value="">Select Province</option>
                  <option value="Gauteng">Gauteng</option>
                  <option value="Western Cape">Western Cape</option>
                  <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                  <option value="Eastern Cape">Eastern Cape</option>
                  <option value="Free State">Free State</option>
                  <option value="Limpopo">Limpopo</option>
                  <option value="Mpumalanga">Mpumalanga</option>
                  <option value="Northern Cape">Northern Cape</option>
                  <option value="North West">North West</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-[#1B0C34] mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                  placeholder="+27821234567"
                />
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div>
            <h3 className="text-lg font-semibold text-[#1B0C34] mb-4">Verification Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                {user?.biometric_verified ? (
                  <><CheckCircle className="w-5 h-5 text-green-600" /> <span className="text-green-600">ID Verified</span></>
                ) : (
                  <><AlertCircle className="w-5 h-5 text-yellow-600" /> <span className="text-yellow-600">ID Not Verified</span></>
                )}
              </div>
              {user?.verification_score && (
                <div>
                  <span className="text-sm text-gray-600">Verification Score: </span>
                  <span className="font-semibold">{user.verification_score}%</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// Postbank Services Component - Groups Cash Withdrawal and Cash Deposit
const PostbankServices = ({ refreshData }) => {
  const [activeTab, setActiveTab] = useState('withdrawal'); // 'withdrawal' or 'deposit'

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-[#1B0C34] flex items-center gap-2">
            <Banknote className="w-6 h-6" />
            Postbank Services
          </CardTitle>
          <CardDescription>Cash withdrawal and deposit services via Postbank SRD integration</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Tab Buttons */}
          <div className="flex gap-2 mb-6">
            <Button
              onClick={() => setActiveTab('withdrawal')}
              variant={activeTab === 'withdrawal' ? 'default' : 'outline'}
              className={`flex-1 ${activeTab === 'withdrawal' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}`}
              data-testid="postbank-tab-withdrawal"
            >
              <Banknote className="w-4 h-4 mr-2" />
              Cash Withdrawal
            </Button>
            <Button
              onClick={() => setActiveTab('deposit')}
              variant={activeTab === 'deposit' ? 'default' : 'outline'}
              className={`flex-1 ${activeTab === 'deposit' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}`}
              data-testid="postbank-tab-deposit"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Cash Deposit
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content Area */}
      {activeTab === 'withdrawal' ? (
        <CashWithdrawal refreshData={refreshData} />
      ) : (
        <CashDeposit refreshData={refreshData} />
      )}
    </div>
  );
};

// Money Send Services Component - Groups Wallet Transfer, ACE Cross-Border, and Money Transfer
const MoneySendServices = ({ refreshData }) => {
  const [activeTab, setActiveTab] = useState('wallet'); // 'wallet', 'ace-cross-border', or 'money-transfer'

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-[#1B0C34] flex items-center gap-2">
            <ArrowRightLeft className="w-6 h-6" />
            Money Send Services
          </CardTitle>
          <CardDescription>Send money via wallet transfer, ACE cross-border, or money transfer</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Tab Buttons */}
          <div className="grid grid-cols-3 gap-2 mb-6">
            <Button
              onClick={() => setActiveTab('wallet')}
              variant={activeTab === 'wallet' ? 'default' : 'outline'}
              className={`${activeTab === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}`}
              data-testid="money-send-tab-wallet"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Wallet Transfer
            </Button>
            <Button
              onClick={() => setActiveTab('ace-cross-border')}
              variant={activeTab === 'ace-cross-border' ? 'default' : 'outline'}
              className={`${activeTab === 'ace-cross-border' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}`}
              data-testid="money-send-tab-ace"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              ACE Cross-Border
            </Button>
            <Button
              onClick={() => setActiveTab('money-transfer')}
              variant={activeTab === 'money-transfer' ? 'default' : 'outline'}
              className={`${activeTab === 'money-transfer' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}`}
              data-testid="money-send-tab-money"
            >
              <Send className="w-4 h-4 mr-2" />
              Money Transfer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content Area */}
      {activeTab === 'wallet' && <WalletTransfer refreshData={refreshData} />}
      {activeTab === 'ace-cross-border' && <ACECrossBorderTransfer refreshData={refreshData} />}
      {activeTab === 'money-transfer' && <MoneyTransfer refreshData={refreshData} />}
    </div>
  );
};

// Dashboard Home Component
const DashboardHome = ({ wallet, transactions }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');
  const verificationStatus = wallet?.verification_status;
  const [showScanner, setShowScanner] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentStep, setPaymentStep] = useState('scan'); // 'scan', 'amount', 'confirm'
  const token = localStorage.getItem('mojo_token');
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleScanComplete = (data) => {
    try {
      let parsedData;
      if (typeof data === 'string') {
        parsedData = JSON.parse(data);
      } else if (data.walletId) {
        parsedData = data;
      } else {
        parsedData = JSON.parse(data.text || data);
      }
      
      setScannedData(parsedData);
      setPaymentStep('amount');
      toast.success(`Customer: ${parsedData.name || 'Unknown'}`);
    } catch (error) {
      toast.error('Invalid QR code format');
    }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setPaymentStep('confirm');
    
    try {
      const response = await axios.post(
        `${API}/wallet/transfer`,
        {
          recipient_wallet_id: scannedData.walletId,
          amount: parseFloat(paymentAmount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Payment received successfully!');
      setShowScanner(false);
      setPaymentStep('scan');
      setScannedData(null);
      setPaymentAmount('');
      
      // Refresh data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setPaymentStep('amount');
    }
  };

  const resetScanner = () => {
    setShowScanner(false);
    setPaymentStep('scan');
    setScannedData(null);
    setPaymentAmount('');
  };
  
  return (
    <div className="space-y-6">
      {/* Verification Status Alert */}
      {verificationStatus && (
        <Card className={`border-2 ${verificationStatus.verified ? 'border-[#145539] bg-[#145539]/5' : 'border-orange-500 bg-orange-50'}`} data-testid="verification-status-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {verificationStatus.verified ? (
                  <CheckCircle className="w-8 h-8 text-[#145539]" />
                ) : (
                  <Shield className="w-8 h-8 text-orange-500" />
                )}
                <div>
                  <p className="font-semibold text-[#1B0C34]">
                    {verificationStatus.verified ? 'ID Verified' : 'ID Verification Required'}
                  </p>
                  {verificationStatus.verified && verificationStatus.score && (
                    <p className="text-sm text-[#1B0C34]/70">
                      Verification Score: {verificationStatus.score}% (VirtuallVerify)
                    </p>
                  )}
                  {!verificationStatus.verified && (
                    <p className="text-sm text-orange-600 mt-1">
                      Complete ID verification to unlock all transactions
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {verificationStatus.verified ? (
                  <span className="px-3 py-1 bg-[#145539] text-white text-xs font-semibold rounded-full">
                    VERIFIED
                  </span>
                ) : (
                  <Button
                    onClick={() => {
                      navigate(`/verify?id_number=${user.id_number || ''}&user_type=merchant`);
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="complete-verification-btn"
                  >
                    <Shield className="mr-2 w-4 h-4" />
                    Complete ID Verification
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white" data-testid="wallet-balance-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardDescription className="text-white/70">Wallet Balance</CardDescription>
                <CardTitle className="text-3xl" data-testid="wallet-balance">R {wallet?.balance?.toFixed(2) || '0.00'}</CardTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowScanner(true)}
                  className="border-[#F2B10C] text-[#F2B10C] hover:bg-[#F2B10C] hover:text-[#1B0C34]"
                  data-testid="scan-qr-btn"
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
                <QRWalletCode 
                  walletId={wallet.wallet_id} 
                  userName={wallet.user_name || user.full_name} 
                  userType="merchant"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Wallet className="w-12 h-12 opacity-20" />
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-[#F2B10C] to-[#d99a0a] text-[#1B0C34]" data-testid="ace-token-card">
          <CardHeader>
            <CardDescription className="text-[#1B0C34]/70">ACE Token Balance</CardDescription>
            <CardTitle className="text-3xl" data-testid="ace-token-balance">{wallet?.ace_token_balance?.toFixed(2) || '0.00'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Coins className="w-12 h-12 opacity-20" />
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-[#145539] to-[#1e7049] text-white" data-testid="transactions-card">
          <CardHeader>
            <CardDescription className="text-white/70">Total Transactions</CardDescription>
            <CardTitle className="text-3xl" data-testid="transaction-count">{transactions?.length || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <TrendingUp className="w-12 h-12 opacity-20" />
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card data-testid="quick-actions-card">
        <CardHeader>
          <CardTitle className="text-[#1B0C34]">Quick Actions</CardTitle>
          <CardDescription>Fast access to common merchant operations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Button
              onClick={() => navigate('/merchant/location')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-[#F2B10C]/10 hover:border-[#F2B10C]"
              data-testid="quick-action-location"
            >
              <MapPin className="w-6 h-6 text-[#145539]" />
              <span className="text-sm font-medium text-[#1B0C34]">Set Location</span>
            </Button>
            
            <Button
              onClick={() => navigate('/merchant/topup')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-[#F2B10C]/10 hover:border-[#F2B10C]"
              data-testid="quick-action-topup"
            >
              <PlusCircle className="w-6 h-6 text-[#145539]" />
              <span className="text-sm font-medium text-[#1B0C34]">Add Funds</span>
            </Button>
            
            <Button
              onClick={() => navigate('/merchant/payout')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-[#F2B10C]/10 hover:border-[#F2B10C]"
              data-testid="quick-action-payout"
            >
              <Send className="w-6 h-6 text-[#145539]" />
              <span className="text-sm font-medium text-[#1B0C34]">Payout to Card</span>
            </Button>
            
            <Button
              onClick={() => navigate('/merchant/customer-verification')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-[#F2B10C]/10 hover:border-[#F2B10C]"
              data-testid="quick-action-verify"
            >
              <UserCheck className="w-6 h-6 text-[#145539]" />
              <span className="text-sm font-medium text-[#1B0C34]">Customer Verify</span>
            </Button>
            
            <Button
              onClick={() => navigate('/merchant/ace-token')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-[#F2B10C]/10 hover:border-[#F2B10C]"
              data-testid="quick-action-ace-token"
            >
              <Coins className="w-6 h-6 text-[#F2B10C]" />
              <span className="text-sm font-medium text-[#1B0C34]">ACE Token</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Merchant Business Information */}
      {wallet?.merchant_id && (
        <Card data-testid="merchant-info-card">
          <CardHeader>
            <CardTitle className="text-[#1B0C34]">Merchant Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#1B0C34]/60">Merchant ID</p>
                <p className="font-mono font-bold text-[#1B0C34]" data-testid="merchant-id">{wallet.merchant_id}</p>
              </div>
              {wallet.business_name && (
                <div>
                  <p className="text-sm text-[#1B0C34]/60">Business Name</p>
                  <p className="font-semibold text-[#1B0C34]">{wallet.business_name}</p>
                </div>
              )}
              {wallet.province && (
                <div>
                  <p className="text-sm text-[#1B0C34]/60">Province</p>
                  <p className="text-[#1B0C34]">{wallet.province}</p>
                </div>
              )}
              {wallet.country && (
                <div>
                  <p className="text-sm text-[#1B0C34]/60">Country</p>
                  <p className="text-[#1B0C34]">{wallet.country}</p>
                </div>
              )}
              {wallet.location?.address && (
                <div className="md:col-span-2">
                  <p className="text-sm text-[#1B0C34]/60">Location</p>
                  <p className="text-sm text-[#1B0C34]">{wallet.location.address}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card data-testid="recent-transactions-card">
        <CardHeader>
          <CardTitle className="text-[#1B0C34]">Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {transactions?.slice(0, 10).map((txn, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-[#FAF5F0] rounded-lg" data-testid={`transaction-${index}`}>
                <div>
                  <p className="font-medium text-[#1B0C34]">{txn.description}</p>
                  <p className="text-sm text-[#1B0C34]/60">{txn.transaction_type.replace('_', ' ').toUpperCase()}</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${txn.amount >= 0 ? 'text-[#145539]' : 'text-red-600'}`}>
                    {txn.amount >= 0 ? '+' : ''}R {Math.abs(txn.amount).toFixed(2)}
                  </p>
                  {txn.fee > 0 && <p className="text-xs text-[#1B0C34]/60">Fee: R {txn.fee.toFixed(2)}</p>}
                </div>
              </div>
            ))}
            {!transactions || transactions.length === 0 && (
              <p className="text-center text-[#1B0C34]/60 py-8">No transactions yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QR Scanner Dialog for Payments */}
      <Dialog open={showScanner} onOpenChange={resetScanner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B0C34]">Scan Customer QR Code</DialogTitle>
            <DialogDescription>Scan customer wallet to receive payment</DialogDescription>
          </DialogHeader>

          {paymentStep === 'scan' && (
            <div className="space-y-4">
              <QRScanner onScanComplete={handleScanComplete} />
            </div>
          )}

          {paymentStep === 'amount' && scannedData && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-800">✓ Customer Scanned</p>
                <p className="text-xs text-green-700 mt-1">
                  {scannedData.name || 'Unknown'} - {scannedData.walletId?.substring(0, 10)}...
                </p>
              </div>

              <div>
                <Label htmlFor="payment_amount">Payment Amount (ZAR)</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  placeholder="Enter amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  data-testid="scan-payment-amount"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={resetScanner}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePaymentSubmit}
                  className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white"
                  disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
                  data-testid="confirm-scan-payment"
                >
                  Confirm Payment
                </Button>
              </div>
            </div>
          )}

          {paymentStep === 'confirm' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F2B10C] mx-auto"></div>
              <p className="mt-4 text-sm text-[#1B0C34]/70">Processing payment...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wallet Top-Up Component
const WalletTopUp = ({ refreshData }) => {
  const [formData, setFormData] = useState({
    amount: '',
    card_type: 'debit',
    card_number: '',
    card_holder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const calculateFee = () => {
    if (!formData.amount) return { fee: 0, net: 0 };
    const amount = parseFloat(formData.amount);
    const feeRate = formData.card_type === 'debit' ? 0.02 : 0.035;
    const fee = amount * feeRate;
    const net = amount - fee;
    return { fee, net };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const last4 = formData.card_number.slice(-4);
      
      const response = await axios.post(
        `${API}/wallet/topup`,
        {
          amount: parseFloat(formData.amount),
          card_type: formData.card_type,
          card_number_last4: last4,
          card_holder_name: formData.card_holder_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Top-up successful! R${response.data.net_credited} added to wallet`);
      setFormData({
        amount: '',
        card_type: 'debit',
        card_number: '',
        card_holder_name: '',
        expiry_month: '',
        expiry_year: '',
        cvv: ''
      });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top-up failed');
    } finally {
      setLoading(false);
    }
  };

  const { fee, net } = calculateFee();

  return (
    <Card data-testid="wallet-topup-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <CreditCard className="inline mr-2" />Add Funds to Wallet
        </CardTitle>
        <CardDescription>Top-up your wallet balance using debit or credit card</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#1B0C34] mb-1">Card Processing Fees</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Debit Card: 2% fee<br/>
            • Credit Card: 3.5% fee<br/>
            • Fee deducted from top-up amount
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="topup_amount">Top-Up Amount (ZAR)</Label>
            <Input
              id="topup_amount"
              type="number"
              step="0.01"
              min="10"
              data-testid="topup-amount-input"
              placeholder="Enter amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-sm">
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Gross Amount:</span>
                  <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Processing Fee ({formData.card_type === 'debit' ? '2%' : '3.5%'}):</span>
                  <span className="text-red-600">-R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#1B0C34]/10 mt-1">
                  <span className="font-semibold text-[#1B0C34]">Net Credited:</span>
                  <span className="font-bold text-[#145539]">R{net.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Card Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="topup-select-debit-btn"
                variant={formData.card_type === 'debit' ? 'default' : 'outline'}
                className={formData.card_type === 'debit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'debit' })}
              >
                Debit (2%)
              </Button>
              <Button
                type="button"
                data-testid="topup-select-credit-btn"
                variant={formData.card_type === 'credit' ? 'default' : 'outline'}
                className={formData.card_type === 'credit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'credit' })}
              >
                Credit (3.5%)
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="card_holder">Card Holder Name</Label>
            <Input
              id="card_holder"
              data-testid="topup-card-holder-input"
              placeholder="Name on card"
              value={formData.card_holder_name}
              onChange={(e) => setFormData({ ...formData, card_holder_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="card_number">Card Number</Label>
            <Input
              id="card_number"
              data-testid="topup-card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                setFormData({ ...formData, card_number: formatted });
              }}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="expiry_month">Month</Label>
              <Input
                id="expiry_month"
                data-testid="topup-expiry-month-input"
                placeholder="MM"
                maxLength="2"
                value={formData.expiry_month}
                onChange={(e) => setFormData({ ...formData, expiry_month: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="expiry_year">Year</Label>
              <Input
                id="expiry_year"
                data-testid="topup-expiry-year-input"
                placeholder="YY"
                maxLength="2"
                value={formData.expiry_year}
                onChange={(e) => setFormData({ ...formData, expiry_year: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cvv">CVV</Label>
              <Input
                id="cvv"
                type="password"
                data-testid="topup-cvv-input"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="submit-topup-btn"
            className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Add Funds to Wallet'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Card Payout Component
const CardPayout = ({ wallet, refreshData }) => {
  const [formData, setFormData] = useState({
    payout_source: 'wallet',
    amount: '',
    ace_amount: '',
    voucher_code: '',
    verification_code: '',
    card_number: '',
    card_holder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    fetchACEInfo();
  }, []);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateFee = () => {
    if (!formData.amount) return { fee: 0, net: 0 };
    const amount = parseFloat(formData.amount);
    const fee = amount * 0.025; // 2.5%
    const net = amount - fee;
    return { fee, net };
  };

  const calculateACEAmount = () => {
    if (!formData.amount || !aceInfo) return '';
    const amount = parseFloat(formData.amount);
    return (amount / aceInfo.current_value_zar).toFixed(6);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        amount: parseFloat(formData.amount),
        payout_source: formData.payout_source,
        card_number: formData.card_number.replace(/\s/g, ''),
        expiry_date: `${formData.expiry_month}/${formData.expiry_year}`,
        cvv: formData.cvv,
        card_holder_name: formData.card_holder_name
      };

      // Add source-specific fields
      if (formData.payout_source === 'ace_token') {
        requestData.ace_amount = parseFloat(formData.ace_amount || calculateACEAmount());
      } else if (formData.payout_source === 'voucher') {
        requestData.voucher_code = formData.voucher_code;
        requestData.verification_code = formData.verification_code;
      }

      const response = await axios.post(
        `${API}/card/payout`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Payout successful! R${response.data.net_payout} sent to card (****${response.data.card_last4})`);
      setFormData({
        payout_source: formData.payout_source,
        amount: '',
        ace_amount: '',
        voucher_code: '',
        verification_code: '',
        card_number: '',
        card_holder_name: '',
        expiry_month: '',
        expiry_year: '',
        cvv: ''
      });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout failed');
    } finally {
      setLoading(false);
    }
  };

  const { fee, net } = calculateFee();

  return (
    <Card data-testid="card-payout-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <Banknote className="inline mr-2" />Payout to Card
        </CardTitle>
        <CardDescription>Withdraw from wallet, vouchers, or ACE tokens to your card</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#1B0C34] mb-1">Payout Fee: 2.5%</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Fee deducted from payout amount<br/>
            • Supports wallet balance, ACE tokens, or vouchers<br/>
            • Processed via Traderoute Gateway
          </p>
        </div>

        {/* Balances Display */}
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-[#FAF5F0] rounded-lg">
          <div>
            <p className="text-xs text-[#1B0C34]/60">Wallet Balance</p>
            <p className="text-sm font-bold text-[#1B0C34]">R{wallet?.balance?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-xs text-[#1B0C34]/60">ACE Balance</p>
            <p className="text-sm font-bold text-[#F2B10C]">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
          </div>
          <div>
            <p className="text-xs text-[#1B0C34]/60">ACE Value</p>
            <p className="text-sm font-bold text-[#145539]">R{aceInfo?.current_value_zar || '0'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Payout Source */}
          <div>
            <Label>Payout Source</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                data-testid="select-wallet-source-btn"
                variant={formData.payout_source === 'wallet' ? 'default' : 'outline'}
                className={formData.payout_source === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, payout_source: 'wallet' })}
              >
                Wallet
              </Button>
              <Button
                type="button"
                data-testid="select-ace-source-btn"
                variant={formData.payout_source === 'ace_token' ? 'default' : 'outline'}
                className={formData.payout_source === 'ace_token' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, payout_source: 'ace_token' })}
              >
                ACE Token
              </Button>
              <Button
                type="button"
                data-testid="select-voucher-source-btn"
                variant={formData.payout_source === 'voucher' ? 'default' : 'outline'}
                className={formData.payout_source === 'voucher' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, payout_source: 'voucher' })}
              >
                Voucher
              </Button>
            </div>
          </div>

          {/* Conditional Fields Based on Source */}
          {formData.payout_source === 'voucher' && (
            <>
              <div>
                <Label htmlFor="payout_voucher_code">Voucher Code</Label>
                <Input
                  id="payout_voucher_code"
                  data-testid="payout-voucher-code-input"
                  placeholder="Enter voucher code"
                  value={formData.voucher_code}
                  onChange={(e) => setFormData({ ...formData, voucher_code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="payout_verification_code">Verification Code</Label>
                <Input
                  id="payout_verification_code"
                  data-testid="payout-verification-code-input"
                  placeholder="4-digit code"
                  maxLength="4"
                  value={formData.verification_code}
                  onChange={(e) => setFormData({ ...formData, verification_code: e.target.value })}
                  required
                />
              </div>
            </>
          )}

          {/* Amount */}
          <div>
            <Label htmlFor="payout_amount">
              {formData.payout_source === 'ace_token' ? 'ACE Amount' : 'Payout Amount (ZAR)'}
            </Label>
            <Input
              id="payout_amount"
              type="number"
              step={formData.payout_source === 'ace_token' ? '0.0001' : '0.01'}
              data-testid="payout-amount-input"
              placeholder={formData.payout_source === 'ace_token' ? 'ACE tokens to convert' : 'ZAR amount'}
              value={formData.payout_source === 'ace_token' ? formData.ace_amount : formData.amount}
              onChange={(e) => {
                if (formData.payout_source === 'ace_token') {
                  const aceAmt = e.target.value;
                  const zarAmt = aceAmt && aceInfo ? (parseFloat(aceAmt) * aceInfo.current_value_zar).toFixed(2) : '';
                  setFormData({ ...formData, ace_amount: aceAmt, amount: zarAmt });
                } else {
                  setFormData({ ...formData, amount: e.target.value });
                }
              }}
              required
            />
            {formData.amount && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-sm">
                {formData.payout_source === 'ace_token' && (
                  <div className="flex justify-between mb-1">
                    <span className="text-[#1B0C34]/60">ZAR Equivalent:</span>
                    <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Gross Amount:</span>
                  <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Payout Fee (2.5%):</span>
                  <span className="text-red-600">-R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#1B0C34]/10 mt-1">
                  <span className="font-semibold text-[#1B0C34]">Net to Card:</span>
                  <span className="font-bold text-[#145539]">R{net.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Card Details */}
          <div>
            <Label htmlFor="payout_card_holder">Card Holder Name</Label>
            <Input
              id="payout_card_holder"
              data-testid="payout-card-holder-input"
              placeholder="Name on card"
              value={formData.card_holder_name}
              onChange={(e) => setFormData({ ...formData, card_holder_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="payout_card_number">Card Number</Label>
            <Input
              id="payout_card_number"
              data-testid="payout-card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                setFormData({ ...formData, card_number: formatted });
              }}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="payout_expiry_month">Month</Label>
              <Input
                id="payout_expiry_month"
                data-testid="payout-expiry-month-input"
                placeholder="MM"
                maxLength="2"
                value={formData.expiry_month}
                onChange={(e) => setFormData({ ...formData, expiry_month: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="payout_expiry_year">Year</Label>
              <Input
                id="payout_expiry_year"
                data-testid="payout-expiry-year-input"
                placeholder="YY"
                maxLength="2"
                value={formData.expiry_year}
                onChange={(e) => setFormData({ ...formData, expiry_year: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="payout_cvv">CVV</Label>
              <Input
                id="payout_cvv"
                type="password"
                data-testid="payout-cvv-input"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="submit-payout-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing Payout...' : 'Payout to Card'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Cash Withdrawal Component (Postbank REST API - JSON-RPC 2.0)
const CashWithdrawal = ({ refreshData }) => {
  const [step, setStep] = useState('search'); // 'search', 'confirm', 'success'
  const [formData, setFormData] = useState({ 
    national_id: '',
    phone_number: '',
    account_number: '',
    amount: ''
  });
  const [customer, setCustomer] = useState(null);
  const [transferId, setTransferId] = useState(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const token = localStorage.getItem('mojo_token');

  const handleCustomerSearch = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/customer-search`,
        { 
          national_id: formData.national_id,
          phone_number: formData.phone_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCustomer(response.data.customer);
        toast.success('Customer found! Select account and enter amount.');
        setStep('confirm');
      } else {
        toast.error(response.data.error || 'Customer not found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Customer search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateWithdrawal = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/withdraw-init`,
        { 
          account_number: formData.account_number,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTransferId(response.data.transfer_id);
        toast.success(response.data.message || 'OTP sent to customer mobile');
      } else {
        toast.error(response.data.error || 'Withdrawal initiation failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmWithdrawal = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/withdraw-confirm`,
        { 
          transfer_id: transferId,
          otp: otp,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResult(response.data);
        setStep('success');
        toast.success('Withdrawal successful!');
        refreshData();
      } else {
        toast.error(response.data.error || 'OTP verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransaction = () => {
    setStep('search');
    setFormData({ national_id: '', phone_number: '', account_number: '', amount: '' });
    setCustomer(null);
    setTransferId(null);
    setOtp('');
    setResult(null);
  };

  return (
    <Card data-testid="cash-withdrawal-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Banknote className="inline mr-2" />Postbank SRD Cash Withdrawal</CardTitle>
        <CardDescription>Withdraw cash from customer Postbank account (Max R370)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            You earn R1.25 commission on every successful withdrawal
          </p>
        </div>

        {step === 'search' && (
          <form onSubmit={handleCustomerSearch} className="space-y-4">
            <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
              <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 1: Customer Search</p>
              <p className="text-xs text-[#1B0C34]/70">
                Enter customer's National ID and Phone Number to search Postbank records
              </p>
            </div>
            
            <div>
              <Label htmlFor="national_id">National ID Number</Label>
              <Input
                id="national_id"
                data-testid="national-id-input"
                placeholder="13-digit SA ID"
                maxLength={13}
                value={formData.national_id}
                onChange={(e) => setFormData({ ...formData, national_id: e.target.value.replace(/\D/g, '') })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                data-testid="phone-number-input"
                placeholder="+27769584862"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">Format: +27XXXXXXXXX</p>
            </div>
            
            <Button
              type="submit"
              data-testid="search-customer-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Customer'}
            </Button>
          </form>
        )}

        {step === 'confirm' && customer && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-semibold text-green-800 mb-2">✓ Customer Found</p>
              <p className="text-xs text-green-700">
                {customer.firstName} {customer.lastName}<br/>
                Phone: {customer.phoneNumber}
              </p>
            </div>

            {!transferId ? (
              <form onSubmit={handleInitiateWithdrawal} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 2: Initiate Withdrawal</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Select account and enter amount. OTP will be sent to customer.
                  </p>
                </div>

                {customer.accounts && customer.accounts.length > 0 && (
                  <div>
                    <Label htmlFor="account_select">Select Account</Label>
                    <select
                      id="account_select"
                      data-testid="account-select"
                      className="w-full p-2 border rounded-md"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                    >
                      <option value="">-- Select Account --</option>
                      {customer.accounts.map((acc, idx) => (
                        <option key={idx} value={acc.accountNumber}>
                          {acc.accountName} - {acc.accountNumber}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="amount">Withdrawal Amount (ZAR)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    max="370"
                    data-testid="withdrawal-amount-input"
                    placeholder="Max R370"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">Maximum R370 per SRD specification</p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('search')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    data-testid="initiate-withdrawal-btn"
                    className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                    disabled={loading}
                  >
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleConfirmWithdrawal} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 3: Confirm with OTP</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Enter the OTP sent to customer's mobile number
                  </p>
                </div>

                <div>
                  <Label htmlFor="otp">OTP Code</Label>
                  <Input
                    id="otp"
                    data-testid="otp-input"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">6-digit code from customer</p>
                </div>
                
                <Button
                  type="submit"
                  data-testid="confirm-withdrawal-btn"
                  className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Confirm Withdrawal'}
                </Button>
              </form>
            )}
          </div>
        )}

        {step === 'success' && result && (
          <div className="space-y-4" data-testid="withdrawal-success">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-2" />
              <h3 className="text-2xl font-bold mb-4">Withdrawal Successful!</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Amount Withdrawn:</span>
                  <span className="font-bold">R{result.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Your Commission:</span>
                  <span className="font-bold text-[#F2B10C]">R{result.commission?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Total Credited:</span>
                  <span className="font-bold text-[#F2B10C]">R{((result.amount || 0) + (result.commission || 0)).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleNewTransaction}
              data-testid="new-withdrawal-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              New Transaction
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Card Payment Component
const CardPayment = ({ refreshData }) => {
  const [formData, setFormData] = useState({ 
    amount: '', 
    card_number: '', 
    expiry_date: '', 
    cvv: '', 
    card_type: 'debit', 
    description: '' 
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/payment/card`,
        { 
          amount: parseFloat(formData.amount),
          card_number: formData.card_number,
          expiry_date: formData.expiry_date,
          cvv: formData.cvv,
          card_type: formData.card_type,
          description: formData.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Payment successful! Fee: ${response.data.fee_rate} • Net: R${response.data.net_amount}`);
      setFormData({ amount: '', card_number: '', expiry_date: '', cvv: '', card_type: 'debit', description: '' });
      refreshData();
    } catch (error) {
      // Handle validation errors properly
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        // Handle Pydantic validation errors
        toast.error(errorMsg.map(err => err.msg).join(', '));
      } else if (errorMsg && typeof errorMsg === 'object') {
        toast.error(JSON.stringify(errorMsg));
      } else {
        toast.error('Payment failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="card-payment-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><CreditCard className="inline mr-2" />Card Payment</CardTitle>
        <CardDescription>Accept card payments via TradeRoute Gateway (MOCKED)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#1B0C34] mb-1">Fee Structure</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Debit Card: 2% processing fee<br/>
            • Credit Card: 3.5% processing fee
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Card Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="select-debit-btn"
                variant={formData.card_type === 'debit' ? 'default' : 'outline'}
                className={formData.card_type === 'debit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'debit' })}
              >
                Debit (2%)
              </Button>
              <Button
                type="button"
                data-testid="select-credit-btn"
                variant={formData.card_type === 'credit' ? 'default' : 'outline'}
                className={formData.card_type === 'credit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'credit' })}
              >
                Credit (3.5%)
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="card_number">Card Number</Label>
            <Input
              id="card_number"
              data-testid="card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => setFormData({ ...formData, card_number: e.target.value.replace(/\s/g, '') })}
              required
            />
            <p className="text-xs text-[#1B0C34]/60 mt-1">16-19 digit card number</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="expiry_date">Expiry Date</Label>
              <Input
                id="expiry_date"
                data-testid="expiry-date-input"
                placeholder="MM/YY"
                maxLength="5"
                value={formData.expiry_date}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length >= 2) {
                    value = value.slice(0, 2) + '/' + value.slice(2, 4);
                  }
                  setFormData({ ...formData, expiry_date: value });
                }}
                required
              />
            </div>
            <div>
              <Label htmlFor="cvv">CVV</Label>
              <Input
                id="cvv"
                data-testid="cvv-input"
                type="password"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value.replace(/\D/g, '') })}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="payment_amount">Amount (ZAR)</Label>
            <Input
              id="payment_amount"
              type="number"
              step="0.01"
              data-testid="card-payment-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && (
              <p className="text-xs text-[#1B0C34]/60 mt-1">
                Fee: R{(parseFloat(formData.amount) * (formData.card_type === 'debit' ? 0.02 : 0.035)).toFixed(2)} • 
                Net: R{(parseFloat(formData.amount) * (formData.card_type === 'debit' ? 0.98 : 0.965)).toFixed(2)}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              data-testid="payment-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="submit-card-payment-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Process Payment'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Cash Deposit Component (Postbank REST API - JSON-RPC 2.0)
const CashDeposit = ({ refreshData }) => {
  const [step, setStep] = useState('search'); // 'search', 'confirm', 'success'
  const [formData, setFormData] = useState({ 
    national_id: '',
    phone_number: '',
    account_number: '',
    amount: ''
  });
  const [customer, setCustomer] = useState(null);
  const [depositId, setDepositId] = useState(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const token = localStorage.getItem('mojo_token');

  const handleCustomerSearch = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/customer-search`,
        { 
          national_id: formData.national_id,
          phone_number: formData.phone_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCustomer(response.data.customer);
        toast.success('Customer found! Select account and enter amount.');
        setStep('confirm');
      } else {
        toast.error(response.data.error || 'Customer not found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Customer search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateDeposit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/deposit-init`,
        { 
          account_number: formData.account_number,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDepositId(response.data.deposit_id);
        toast.success(response.data.message || 'OTP sent to customer mobile');
      } else {
        toast.error(response.data.error || 'Deposit initiation failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deposit initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeposit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/deposit-confirm`,
        { 
          deposit_id: depositId,
          otp: otp,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResult(response.data);
        setStep('success');
        toast.success('Deposit successful!');
        refreshData();
      } else {
        toast.error(response.data.error || 'OTP verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deposit confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransaction = () => {
    setStep('search');
    setFormData({ national_id: '', phone_number: '', account_number: '', amount: '' });
    setCustomer(null);
    setDepositId(null);
    setOtp('');
    setResult(null);
  };

  return (
    <Card data-testid="cash-deposit-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Banknote className="inline mr-2" />Postbank Cash Deposit</CardTitle>
        <CardDescription>Deposit cash into customer Postbank account</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            You earn R1.25 commission for every cash deposit. Cash will be deducted from your wallet.
          </p>
        </div>

        {step === 'search' && (
          <form onSubmit={handleCustomerSearch} className="space-y-4">
            <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
              <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 1: Customer Search</p>
              <p className="text-xs text-[#1B0C34]/70">
                Enter customer National ID and Phone Number to search Postbank records
              </p>
            </div>
            
            <div>
              <Label htmlFor="dep_national_id">National ID Number</Label>
              <Input
                id="dep_national_id"
                data-testid="deposit-national-id-input"
                placeholder="13-digit SA ID"
                maxLength={13}
                value={formData.national_id}
                onChange={(e) => setFormData({ ...formData, national_id: e.target.value.replace(/\D/g, '') })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="dep_phone_number">Phone Number</Label>
              <Input
                id="dep_phone_number"
                data-testid="deposit-phone-number-input"
                placeholder="+27769584862"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">Format: +27XXXXXXXXX</p>
            </div>
            
            <Button
              type="submit"
              data-testid="search-customer-deposit-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Customer'}
            </Button>
          </form>
        )}

        {step === 'confirm' && customer && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-semibold text-green-800 mb-2">✓ Customer Found</p>
              <p className="text-xs text-green-700">
                {customer.firstName} {customer.lastName}<br/>
                Phone: {customer.phoneNumber}
              </p>
            </div>

            {!depositId ? (
              <form onSubmit={handleInitiateDeposit} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 2: Initiate Deposit</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Select account and enter amount. OTP will be sent to customer.
                  </p>
                </div>

                {customer.accounts && customer.accounts.length > 0 && (
                  <div>
                    <Label htmlFor="deposit_account_select">Select Account</Label>
                    <select
                      id="deposit_account_select"
                      data-testid="deposit-account-select"
                      className="w-full p-2 border rounded-md"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                    >
                      <option value="">-- Select Account --</option>
                      {customer.accounts.map((acc, idx) => (
                        <option key={idx} value={acc.accountNumber}>
                          {acc.accountName} - {acc.accountNumber}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="deposit_amount">Deposit Amount (ZAR)</Label>
                  <Input
                    id="deposit_amount"
                    type="number"
                    step="0.01"
                    data-testid="deposit-amount-input"
                    placeholder="Enter amount"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">Cash will be deducted from your wallet</p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('search')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    data-testid="initiate-deposit-btn"
                    className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                    disabled={loading}
                  >
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleConfirmDeposit} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 3: Confirm with OTP</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Enter the OTP sent to customer mobile number
                  </p>
                </div>

                <div>
                  <Label htmlFor="deposit_otp">OTP Code</Label>
                  <Input
                    id="deposit_otp"
                    data-testid="deposit-otp-input"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">6-digit code from customer</p>
                </div>
                
                <Button
                  type="submit"
                  data-testid="confirm-deposit-btn"
                  className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Confirm Deposit'}
                </Button>
              </form>
            )}
          </div>
        )}

        {step === 'success' && result && (
          <div className="space-y-4" data-testid="deposit-success">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-2" />
              <h3 className="text-2xl font-bold mb-4">Deposit Successful!</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Amount Deposited:</span>
                  <span className="font-bold">R{result.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Your Commission:</span>
                  <span className="font-bold text-[#F2B10C]">R{result.commission?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Net Deducted:</span>
                  <span className="font-bold">R{result.net_deducted?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>New Balance Impact:</span>
                  <span className="font-bold text-red-300">-R{result.net_deducted?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleNewTransaction}
              data-testid="new-deposit-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              New Transaction
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Wallet Transfer Component
const WalletTransfer = ({ refreshData }) => {
  const [formData, setFormData] = useState({ recipient_wallet_id: '', amount: '', description: '' });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/wallet/transfer`,
        {
          recipient_wallet_id: formData.recipient_wallet_id,
          amount: parseFloat(formData.amount),
          description: formData.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Transfer successful!');
      setFormData({ recipient_wallet_id: '', amount: '', description: '' });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="wallet-transfer-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Wallet className="inline mr-2" />Wallet to Wallet Transfer</CardTitle>
        <CardDescription>Transfer funds to another Mojo Money wallet</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="recipient_wallet_id">Recipient Wallet ID</Label>
            <Input
              id="recipient_wallet_id"
              data-testid="recipient-wallet-id-input"
              placeholder="MWXXXXXXXXXXXX"
              value={formData.recipient_wallet_id}
              onChange={(e) => setFormData({ ...formData, recipient_wallet_id: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="transfer_amount">Amount (ZAR)</Label>
            <Input
              id="transfer_amount"
              type="number"
              step="0.01"
              data-testid="transfer-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="transfer_description">Description (Optional)</Label>
            <Input
              id="transfer_description"
              data-testid="transfer-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <Button
            type="submit"
            data-testid="submit-transfer-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Send Transfer'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ACE Cross-Border Transfer Component
const ACECrossBorderTransfer = ({ refreshData }) => {
  const [formData, setFormData] = useState({
    recipient_identifier: '',
    recipient_type: 'phone',
    amount: '',
    recipient_currency: 'ZAR',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [limits, setLimits] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [aceInfo, setAceInfo] = useState(null);
  const [showPhonebook, setShowPhonebook] = useState(false);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    fetchLimits();
    fetchCurrencies();
    fetchACEInfo();
  }, []);

  const handleContactSelect = (contact) => {
    setFormData({ 
      ...formData, 
      recipient_identifier: contact.phone_number,
      recipient_type: 'phone'
    });
    toast.success(`Selected: ${contact.name}`);
  };

  const fetchLimits = async () => {
    try {
      const response = await axios.get(`${API}/ace-transfer/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch limits');
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/ace-transfer/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error('Failed to fetch currencies');
    }
  };

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateFee = () => {
    if (!formData.amount) return 0;
    const amount = parseFloat(formData.amount);
    const percentageFee = amount * 0.035; // 3.5%
    return Math.min(30, percentageFee);
  };

  const calculateACE = () => {
    if (!formData.amount || !aceInfo) return 0;
    const amount = parseFloat(formData.amount);
    return (amount / aceInfo.current_value_zar).toFixed(6);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ace-transfer/cross-border`,
        {
          recipient_identifier: formData.recipient_identifier,
          recipient_type: formData.recipient_type,
          amount: parseFloat(formData.amount),
          recipient_currency: formData.recipient_currency,
          description: formData.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success(response.data.message || 'Transfer successful!');
      
      // Clear form
      setFormData({
        recipient_identifier: '',
        recipient_type: formData.recipient_type,
        amount: '',
        recipient_currency: formData.recipient_currency,
        description: ''
      });
      refreshData();
      fetchLimits(); // Refresh limits after transfer
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const fee = calculateFee();
  const aceAmount = calculateACE();

  return (
    <Card data-testid="ace-cross-border-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <ArrowRightLeft className="inline mr-2" />Send Money Across Africa with ACE
        </CardTitle>
        <CardDescription>Cross-border transfers using ACE stable coin via Interledger Protocol</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Limits & Info Banner */}
        {limits && (
          <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
            <p className="text-sm font-medium text-[#145539] mb-1">
              💰 Monthly Limit: R{limits.monthly_remaining.toFixed(2)} remaining of R{limits.monthly_limit.toFixed(2)}
            </p>
            <p className="text-xs text-[#1B0C34]/70">
              • Fee: Lesser of R30 or 3.5% per transfer<br/>
              • {limits.verification_status ? '✅ ID Verified (R25,000 limit)' : '⚠️ Not Verified (R14,000 limit - Complete ID verification for R25,000)'}<br/>
              • ACE Token Value: R{aceInfo?.current_value_zar || '0'} per token
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recipient Type */}
          <div>
            <Label>Recipient Identification</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                data-testid="select-phone-btn"
                variant={formData.recipient_type === 'phone' ? 'default' : 'outline'}
                className={formData.recipient_type === 'phone' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, recipient_type: 'phone' })}
              >
                Phone
              </Button>
              <Button
                type="button"
                data-testid="select-email-btn"
                variant={formData.recipient_type === 'email' ? 'default' : 'outline'}
                className={formData.recipient_type === 'email' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, recipient_type: 'email' })}
              >
                Email
              </Button>
              <Button
                type="button"
                data-testid="select-wallet-btn"
                variant={formData.recipient_type === 'wallet_id' ? 'default' : 'outline'}
                className={formData.recipient_type === 'wallet_id' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, recipient_type: 'wallet_id' })}
              >
                Wallet ID
              </Button>
            </div>
          </div>

          {/* Recipient Identifier */}
          <div>
            <Label htmlFor="recipient_identifier">
              {formData.recipient_type === 'phone' ? 'Recipient Phone Number' :
               formData.recipient_type === 'email' ? 'Recipient Email' : 'Recipient Wallet ID'}
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="recipient_identifier"
                data-testid="recipient-identifier-input"
                placeholder={
                  formData.recipient_type === 'phone' ? '+27XXXXXXXXX' :
                  formData.recipient_type === 'email' ? 'user@example.com' : 'MWXXXXXXXXXX'
                }
                value={formData.recipient_identifier}
                onChange={(e) => setFormData({ ...formData, recipient_identifier: e.target.value })}
                required
                className="flex-1"
              />
              {formData.recipient_type === 'phone' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPhonebook(true)}
                  className="shrink-0"
                  data-testid="open-phonebook-ace-btn"
                >
                  <Users className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Amount */}
          <div>
            <Label htmlFor="ace_amount">Amount (ZAR)</Label>
            <Input
              id="ace_amount"
              type="number"
              step="0.01"
              data-testid="ace-amount-input"
              placeholder="Enter amount to send"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && aceInfo && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Send Amount:</span>
                  <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">ACE Tokens:</span>
                  <span className="font-semibold text-[#145539]">{aceAmount} ACE</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Transfer Fee:</span>
                  <span className="text-red-600">R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#1B0C34]/10 mt-1">
                  <span className="font-semibold text-[#1B0C34]">Total Deducted:</span>
                  <span className="font-bold text-red-600">R{(parseFloat(formData.amount) + fee).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Recipient Currency */}
          <div>
            <Label htmlFor="recipient_currency">Recipient Currency</Label>
            <Select
              value={formData.recipient_currency}
              onValueChange={(value) => setFormData({ ...formData, recipient_currency: value })}
            >
              <SelectTrigger data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    {curr.symbol} {curr.code} - {curr.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#1B0C34]/60 mt-1">
              Recipient will receive ACE tokens (can convert to {formData.recipient_currency})
            </p>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              data-testid="description-input"
              placeholder="Purpose of transfer"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <Button
            type="submit"
            data-testid="submit-ace-transfer-btn"
            className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing via Interledger...' : 'Send Money via ACE'}
          </Button>
        </form>

        {/* Success Result */}
        {result && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="ace-transfer-result">
            <p className="font-bold text-lg mb-2">✅ Transfer Successful!</p>
            <div className="space-y-2 text-sm">
              <div className="p-2 bg-white/10 rounded">
                <p className="text-xs opacity-70">Transaction ID</p>
                <p className="font-mono text-xs">{result.ilp_transaction_id}</p>
              </div>
              <div className="flex justify-between">
                <span>Amount Sent:</span>
                <span className="font-bold">R{result.amount_sent?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>ACE Tokens:</span>
                <span className="font-bold text-[#F2B10C]">{result.ace_amount} ACE</span>
              </div>
              <div className="flex justify-between">
                <span>Recipient Gets:</span>
                <span className="font-bold">{result.recipient?.currency} {result.recipient?.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Transfer Fee:</span>
                <span>R{result.transfer_fee?.toFixed(2)}</span>
              </div>
              <div className="pt-2 border-t border-white/20">
                <p className="text-xs opacity-70">Recipient: {result.recipient?.identifier}</p>
                <p className="text-xs opacity-70">Protocol: {result.protocol}</p>
                <p className="text-xs opacity-70">Monthly Remaining: R{result.limits?.monthly_remaining?.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Phonebook Picker */}
      <PhonebookPicker
        open={showPhonebook}
        onClose={() => setShowPhonebook(false)}
        onSelectContact={handleContactSelect}
      />
    </Card>
  );
};

// Money Transfer Component
const MoneyTransfer = ({ refreshData }) => {
  const [formData, setFormData] = useState({ 
    recipient_phone: '', 
    amount: '', 
    transfer_type: 'send',
    voucher_code: '',
    verification_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showPhonebook, setShowPhonebook] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleContactSelect = (contact) => {
    setFormData({ ...formData, recipient_phone: contact.phone_number });
    toast.success(`Selected: ${contact.name}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        recipient_phone: formData.recipient_phone,
        amount: parseFloat(formData.amount),
        transfer_type: formData.transfer_type
      };

      // Add voucher details if redeeming
      if (formData.transfer_type === 'redeem') {
        requestData.voucher_code = formData.voucher_code;
        requestData.verification_code = formData.verification_code;
      }

      const response = await axios.post(
        `${API}/money-transfer`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success(response.data.message || 'Transaction successful!');
      
      // Clear form except transfer type
      setFormData({ 
        ...formData,
        recipient_phone: '', 
        amount: '',
        voucher_code: '',
        verification_code: ''
      });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="money-transfer-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><ArrowRightLeft className="inline mr-2" />Money Transfer</CardTitle>
        <CardDescription>Send or redeem money transfers using Traderoute vouchers</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R2.00 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Send: Transfer fee R7 per R3000 + R2 commission<br/>
            • Redeem: R2 commission per redemption<br/>
            • {result?.mocked === false ? '✅ Live Traderoute Voucher System' : '🔄 Mock Mode'}
          </p>
        </div>

        <div>
          <Label>Transfer Type</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="select-send-btn"
              variant={formData.transfer_type === 'send' ? 'default' : 'outline'}
              className={formData.transfer_type === 'send' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => {
                setFormData({ ...formData, transfer_type: 'send' });
                setResult(null);
              }}
            >
              Send
            </Button>
            <Button
              type="button"
              data-testid="select-redeem-btn"
              variant={formData.transfer_type === 'redeem' ? 'default' : 'outline'}
              className={formData.transfer_type === 'redeem' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => {
                setFormData({ ...formData, transfer_type: 'redeem' });
                setResult(null);
              }}
            >
              Redeem
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {formData.transfer_type === 'send' ? (
            <>
              <div>
                <Label htmlFor="recipient_phone">Recipient Phone Number</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="recipient_phone"
                    data-testid="recipient-phone-input"
                    placeholder="+27XXXXXXXXX"
                    value={formData.recipient_phone}
                    onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                    required
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPhonebook(true)}
                    className="shrink-0"
                    data-testid="open-phonebook-btn"
                  >
                    <Users className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="mt_amount">Amount (ZAR)</Label>
                <Input
                  id="mt_amount"
                  type="number"
                  step="0.01"
                  data-testid="money-transfer-amount-input"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
                {formData.amount && (
                  <p className="text-xs text-[#1B0C34]/60 mt-1">
                    Transfer fee: R{((parseFloat(formData.amount) / 3000) * 7).toFixed(2)}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <Label htmlFor="voucher_code">Voucher Code</Label>
                <Input
                  id="voucher_code"
                  data-testid="voucher-code-input"
                  placeholder="Enter 16-digit voucher code"
                  value={formData.voucher_code}
                  onChange={(e) => setFormData({ ...formData, voucher_code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="verification_code">Verification Code</Label>
                <Input
                  id="verification_code"
                  data-testid="verification-code-input"
                  placeholder="Enter 4-digit code"
                  maxLength="4"
                  value={formData.verification_code}
                  onChange={(e) => setFormData({ ...formData, verification_code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="redeem_amount">Amount to Redeem (ZAR)</Label>
                <Input
                  id="redeem_amount"
                  type="number"
                  step="0.01"
                  data-testid="redeem-amount-input"
                  placeholder="Full or partial amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
                <p className="text-xs text-[#1B0C34]/60 mt-1">
                  Enter the amount you want to redeem (can be partial)
                </p>
              </div>
              <div>
                <Label htmlFor="recipient_phone_redeem">Recipient Phone (Optional)</Label>
                <Input
                  id="recipient_phone_redeem"
                  placeholder="+27XXXXXXXXX"
                  value={formData.recipient_phone}
                  onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                />
              </div>
            </>
          )}

          <Button
            type="submit"
            data-testid="submit-money-transfer-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Processing...' : (formData.transfer_type === 'send' ? 'Send Money' : 'Redeem Voucher')}
          </Button>
        </form>

        {result && formData.transfer_type === 'send' && (result.voucher_code || result.transfer_code) && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="transfer-code-result">
            <p className="font-bold text-lg mb-2">✅ Voucher Created!</p>
            <div className="space-y-2 text-sm">
              <div className="p-2 bg-white/10 rounded">
                <p className="text-xs opacity-70">Voucher Code</p>
                <p className="font-mono font-bold text-base">{result.voucher_code || result.transfer_code}</p>
              </div>
              <div className="p-2 bg-white/10 rounded">
                <p className="text-xs opacity-70">Verification Code</p>
                <p className="font-mono font-bold text-base">{result.verification_code}</p>
              </div>
              {result.expiry_date && (
                <div className="p-2 bg-white/10 rounded">
                  <p className="text-xs opacity-70">Expires</p>
                  <p className="text-sm">{new Date(result.expiry_date).toLocaleString()}</p>
                </div>
              )}
              <div className="pt-2 border-t border-white/20">
                <p className="text-xs opacity-70">Amount</p>
                <p className="font-semibold">R{result.amount?.toFixed(2)}</p>
              </div>
              <div className="text-xs opacity-80 mt-3 p-2 bg-[#F2B10C]/20 rounded">
                📱 Share both codes with {result.recipient_phone} to redeem
              </div>
            </div>
          </div>
        )}

        {result && formData.transfer_type === 'redeem' && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="redeem-result">
            <p className="font-bold text-lg mb-2">✅ Voucher Redeemed!</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Amount Redeemed:</span>
                <span className="font-bold">R{result.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Commission:</span>
                <span className="font-bold text-[#F2B10C]">+R{result.merchant_commission?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/20">
                <span>Total Credited:</span>
                <span className="font-bold text-lg">R{result.total_credited?.toFixed(2)}</span>
              </div>
              {result.remaining_balance !== undefined && result.remaining_balance > 0 && (
                <div className="mt-2 p-2 bg-white/10 rounded">
                  <p className="text-xs opacity-70">Voucher Remaining Balance</p>
                  <p className="font-semibold">R{result.remaining_balance.toFixed(2)}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {/* Phonebook Picker */}
      <PhonebookPicker
        open={showPhonebook}
        onClose={() => setShowPhonebook(false)}
        onSelectContact={handleContactSelect}
      />
    </Card>
  );
};

// ACE Token Component
const ACEToken = ({ wallet, refreshData }) => {
  const [formData, setFormData] = useState({ amount: '', operation: 'buy' });
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    fetchACEInfo();
  }, []);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateTokens = () => {
    if (!formData.amount || !aceInfo) return 0;
    if (formData.operation === 'buy') {
      return (parseFloat(formData.amount) / aceInfo.current_value_zar).toFixed(4);
    }
    return (parseFloat(formData.amount) * aceInfo.current_value_zar).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ace-token`,
        {
          amount: parseFloat(formData.amount),
          operation: formData.operation
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const tokensAmount = formData.operation === 'buy' ? response.data.ace_tokens : response.data.zar_received;
      toast.success(`Success! ${formData.operation === 'buy' ? `Received ${tokensAmount} ACE` : `Received R${tokensAmount}`}`);
      setFormData({ amount: '', operation: formData.operation });
      refreshData();
      fetchACEInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="ace-token-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Coins className="inline mr-2" />ACE Token Trading</CardTitle>
        <CardDescription>Carbon tax-backed tokens on Hedera DLT (2.5 year minting cycles)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {aceInfo && (
          <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="text-sm opacity-80">Current ACE Token Value</p>
                <p className="text-3xl font-bold">R {aceInfo.current_value_zar}</p>
                <p className="text-xs opacity-70 mt-1">Based on SA Carbon Tax (R{aceInfo.carbon_tax_rate}/tonne - 25%)</p>
              </div>
              <div className="text-right">
                <p className="text-xs opacity-80">Cycle {aceInfo.minting_cycle_years}yr</p>
                <p className="text-sm font-semibold">{aceInfo.days_remaining_in_cycle} days left</p>
              </div>
            </div>
            <div className="pt-3 border-t border-white/20">
              <p className="text-xs opacity-80">💹 Value appreciates with annual carbon tax increases</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 p-4 bg-[#FAF5F0] rounded-lg">
          <div>
            <p className="text-sm text-[#1B0C34]/60">Wallet Balance</p>
            <p className="text-xl font-bold text-[#1B0C34]">R {wallet?.balance?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-sm text-[#1B0C34]/60">ACE Token Balance</p>
            <p className="text-xl font-bold text-[#F2B10C]">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
          </div>
        </div>

        <div>
          <Label>Operation</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="select-buy-btn"
              variant={formData.operation === 'buy' ? 'default' : 'outline'}
              className={formData.operation === 'buy' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => setFormData({ ...formData, operation: 'buy' })}
            >
              Buy ACE
            </Button>
            <Button
              type="button"
              data-testid="select-redeem-ace-btn"
              variant={formData.operation === 'redeem' ? 'default' : 'outline'}
              className={formData.operation === 'redeem' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => setFormData({ ...formData, operation: 'redeem' })}
            >
              Redeem ACE
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="ace_amount">Amount ({formData.operation === 'buy' ? 'ZAR to Spend' : 'ACE Tokens'})</Label>
            <Input
              id="ace_amount"
              type="number"
              step="0.01"
              data-testid="ace-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && aceInfo && (
              <p className="text-sm text-[#145539] font-medium mt-2">
                ≈ {formData.operation === 'buy' 
                  ? `${calculateTokens()} ACE tokens` 
                  : `R${calculateTokens()} ZAR`}
              </p>
            )}
            {aceInfo && (
              <p className="text-xs text-[#1B0C34]/60 mt-1">
                1 ACE = R{aceInfo.current_value_zar} ZAR (Carbon-backed)
              </p>
            )}
          </div>
          <Button
            type="submit"
            data-testid="submit-ace-token-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing on Hedera DLT...' : (formData.operation === 'buy' ? 'Buy ACE Tokens' : 'Redeem ACE Tokens')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Airtime & Data Component
const AirtimeData = ({ refreshData }) => {
  const [formData, setFormData] = useState({
    phone_number: '',
    product_type: 'airtime',
    amount: '',
    provider: 'MTN'
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/airtime-data`,
        {
          phone_number: formData.phone_number,
          product_type: formData.product_type,
          amount: parseFloat(formData.amount),
          provider: formData.provider
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Purchase successful! Voucher: ${response.data.voucher_code}`);
      setFormData({ ...formData, phone_number: '', amount: '' });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="airtime-data-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Smartphone className="inline mr-2" />Airtime & Data</CardTitle>
        <CardDescription>Purchase airtime or data via TradeRoute Voucher System (MOCKED)</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Product Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="select-airtime-btn"
                variant={formData.product_type === 'airtime' ? 'default' : 'outline'}
                className={formData.product_type === 'airtime' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, product_type: 'airtime' })}
              >
                Airtime
              </Button>
              <Button
                type="button"
                data-testid="select-data-btn"
                variant={formData.product_type === 'data' ? 'default' : 'outline'}
                className={formData.product_type === 'data' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, product_type: 'data' })}
              >
                Data
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="provider">Provider</Label>
            <Select value={formData.provider} onValueChange={(value) => setFormData({ ...formData, provider: value })}>
              <SelectTrigger data-testid="provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MTN">MTN</SelectItem>
                <SelectItem value="Vodacom">Vodacom</SelectItem>
                <SelectItem value="Cell C">Cell C</SelectItem>
                <SelectItem value="Telkom">Telkom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="phone_number">Phone Number</Label>
            <Input
              id="phone_number"
              data-testid="airtime-phone-input"
              placeholder="+27XXXXXXXXX"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="ad_amount">Amount (ZAR)</Label>
            <Input
              id="ad_amount"
              type="number"
              step="0.01"
              data-testid="airtime-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="submit-airtime-data-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Processing...' : `Purchase ${formData.product_type}`}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Customer ID Verification Component
const CustomerIDVerification = ({ refreshData }) => {
  const [step, setStep] = useState('details'); // 'details', 'verify', 'result'
  const [formData, setFormData] = useState({
    customer_name: '',
    id_type: 'id_card',
    id_number: '',
    phone_number: ''
  });
  const [sessionData, setSessionData] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleInitiate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/customer-verification/initiate`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSessionData(response.data);
      setStep('verify');
      toast.success('Verification session initiated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate verification');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenVerification = () => {
    if (!sessionData?.verification_url) return;

    // Open VirtuallVerify in popup
    const width = 800;
    const height = 900;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);
    
    const verifyWindow = window.open(
      sessionData.verification_url,
      'CustomerVerification',
      `width=${width},height=${height},top=${top},left=${left}`
    );

    toast.info('Customer should complete face capture and ID verification');

    // Poll for window close
    const pollTimer = setInterval(() => {
      if (verifyWindow && verifyWindow.closed) {
        clearInterval(pollTimer);
        toast.info('Verification window closed. Click "Complete Verification" when ready.');
      }
    }, 1000);
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/customer-verification/complete`,
        {
          customer_name: formData.customer_name,
          id_number: formData.id_number,
          verification_token: sessionData.access_token,
          delivery_method: deliveryMethod,
          delivery_address: deliveryAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.verified) {
        setVerificationResult(response.data);
        setStep('result');
        toast.success(`Verification successful! Commission: R${response.data.commission_earned}`);
        refreshData();
      } else {
        toast.error(response.data.message || 'Verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification completion failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewVerification = () => {
    setStep('details');
    setFormData({ customer_name: '', id_type: 'id_card', id_number: '', phone_number: '' });
    setSessionData(null);
    setDeliveryAddress('');
    setVerificationResult(null);
  };

  return (
    <Card data-testid="customer-verification-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <UserCheck className="inline mr-2" />Customer ID Verification
        </CardTitle>
        <CardDescription>Verify customer identity with face capture and ID matching</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per verification</p>
          <p className="text-xs text-[#1B0C34]/70">
            ✓ Face capture with liveness detection<br/>
            ✓ ID/Passport document validation<br/>
            ✓ 80% minimum match threshold
          </p>
        </div>

        {step === 'details' && (
          <form onSubmit={handleInitiate} className="space-y-4">
            <div>
              <Label htmlFor="customer_name">Customer Full Name</Label>
              <Input
                id="customer_name"
                data-testid="customer-name-input"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>ID Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="select-id-card-btn"
                  variant={formData.id_type === 'id_card' ? 'default' : 'outline'}
                  className={formData.id_type === 'id_card' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setFormData({ ...formData, id_type: 'id_card' })}
                >
                  SA ID Card
                </Button>
                <Button
                  type="button"
                  data-testid="select-passport-btn"
                  variant={formData.id_type === 'passport' ? 'default' : 'outline'}
                  className={formData.id_type === 'passport' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setFormData({ ...formData, id_type: 'passport' })}
                >
                  Passport
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="id_number">
                {formData.id_type === 'id_card' ? 'ID Number' : 'Passport Number'}
              </Label>
              <Input
                id="id_number"
                data-testid="customer-id-number-input"
                placeholder={formData.id_type === 'id_card' ? '13 digits' : 'Passport number'}
                value={formData.id_number}
                onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="phone_number">Customer Phone Number</Label>
              <Input
                id="phone_number"
                data-testid="customer-phone-input"
                placeholder="+27XXXXXXXXX"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="initiate-verification-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Initiating...' : 'Initiate Verification'}
            </Button>
          </form>
        )}

        {step === 'verify' && sessionData && (
          <div className="space-y-4">
            <div className="p-4 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
              <h3 className="font-semibold text-[#1B0C34] mb-2">Verification Session Active</h3>
              <p className="text-sm text-[#1B0C34]/70 mb-3">
                Customer: <strong>{formData.customer_name}</strong><br/>
                ID: <strong>{formData.id_number}</strong>
              </p>
              <Button
                onClick={handleOpenVerification}
                data-testid="open-verification-btn"
                className="w-full bg-[#1B0C34] hover:bg-[#2D1854] text-white mb-2"
              >
                Open VirtuallVerify Portal
              </Button>
              <p className="text-xs text-[#1B0C34]/60 text-center">
                Customer will complete:<br/>
                1. Face capture with liveness detection<br/>
                2. ID/Passport document scan<br/>
                3. Face-to-ID matching
              </p>
            </div>

            <form onSubmit={handleComplete} className="space-y-4">
              <div>
                <Label>Certificate Delivery Method</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    data-testid="select-wallet-cert-btn"
                    variant={deliveryMethod === 'wallet' ? 'default' : 'outline'}
                    className={deliveryMethod === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('wallet')}
                  >
                    Wallet
                  </Button>
                  <Button
                    type="button"
                    data-testid="select-email-cert-btn"
                    variant={deliveryMethod === 'email' ? 'default' : 'outline'}
                    className={deliveryMethod === 'email' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('email')}
                  >
                    Email
                  </Button>
                  <Button
                    type="button"
                    data-testid="select-whatsapp-cert-btn"
                    variant={deliveryMethod === 'whatsapp' ? 'default' : 'outline'}
                    className={deliveryMethod === 'whatsapp' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('whatsapp')}
                  >
                    WhatsApp
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="cert_delivery_address">
                  {deliveryMethod === 'wallet' ? 'Customer Wallet ID' : 
                   deliveryMethod === 'email' ? 'Customer Email' : 'Customer WhatsApp'}
                </Label>
                <Input
                  id="cert_delivery_address"
                  data-testid="cert-delivery-address-input"
                  placeholder={
                    deliveryMethod === 'wallet' ? 'MWXXXXXXXXXXXX' :
                    deliveryMethod === 'email' ? 'customer@email.com' :
                    '+27XXXXXXXXX'
                  }
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="complete-verification-btn"
                className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                disabled={loading}
              >
                {loading ? 'Completing...' : 'Complete Verification'}
              </Button>
            </form>
          </div>
        )}

        {step === 'result' && verificationResult && (
          <div className="space-y-4" data-testid="verification-result">
            <div className="p-6 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg">
              <div className="text-center mb-4">
                <CheckCircle className="w-20 h-20 mx-auto mb-3" />
                <h3 className="text-3xl font-bold">ID VERIFIED ✓</h3>
              </div>

              <div className="space-y-3 text-sm">
                <div className="p-3 bg-white/10 rounded">
                  <p className="text-xs opacity-70 mb-1">Certificate Number</p>
                  <p className="font-mono font-bold text-lg">{verificationResult.certificate.certificate_number}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white/10 rounded">
                    <p className="text-xs opacity-70">Customer</p>
                    <p className="font-semibold">{verificationResult.certificate.customer_name}</p>
                  </div>
                  <div className="p-3 bg-white/10 rounded">
                    <p className="text-xs opacity-70">ID Number</p>
                    <p className="font-mono text-sm">{verificationResult.certificate.id_number}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 bg-white/10 rounded text-center">
                    <p className="text-xs opacity-70">Overall</p>
                    <p className="font-bold text-[#F2B10C]">{verificationResult.verification_score.toFixed(1)}%</p>
                  </div>
                  <div className="p-2 bg-white/10 rounded text-center">
                    <p className="text-xs opacity-70">Face Match</p>
                    <p className="font-bold">{verificationResult.face_match_score.toFixed(1)}%</p>
                  </div>
                  <div className="p-2 bg-white/10 rounded text-center">
                    <p className="text-xs opacity-70">ID Valid</p>
                    <p className="font-bold">{verificationResult.id_validation_score.toFixed(1)}%</p>
                  </div>
                </div>

                <div className="pt-3 border-t border-white/20">
                  <div className="flex justify-between items-center">
                    <span>Your Commission:</span>
                    <span className="font-bold text-[#F2B10C] text-xl">R{verificationResult.commission_earned.toFixed(2)}</span>
                  </div>
                </div>

                <div className="p-3 bg-white/10 rounded">
                  <p className="text-xs font-semibold mb-1">Certificate Delivered</p>
                  <p className="text-xs">{verificationResult.delivery_status}</p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleNewVerification}
              data-testid="new-verification-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              Verify Another Customer
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Insurance Premium Collection Component
const InsurancePremium = ({ refreshData }) => {
  const [step, setStep] = useState('lookup'); // 'lookup', 'payment', 'collect', 'receipt'
  const [lookupType, setLookupType] = useState('id_number');
  const [lookupValue, setLookupValue] = useState('');
  const [policyData, setPolicyData] = useState(null);
  const [premiumAmount, setPremiumAmount] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [walletBalance, setWalletBalance] = useState(0);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardType: 'debit'
  });
  const token = localStorage.getItem('mojo_token');

  const fetchWalletBalance = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWalletBalance(response.data.balance || 0);
    } catch (error) {
      console.error('Failed to fetch wallet balance', error);
      setWalletBalance(0);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/insurance/lookup?lookup_type=${lookupType}&lookup_value=${lookupValue}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPolicyData(response.data);
      setPremiumAmount(response.data.premium_due.toString());
      
      // Fetch wallet balance before showing payment dialog
      await fetchWalletBalance();
      setShowPaymentDialog(true);
      toast.success('Policy found!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Policy not found');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodSelected = async () => {
    // If card payment, show card input dialog first
    if (selectedPaymentMethod === 'card') {
      setShowPaymentDialog(false);
      setShowCardDialog(true);
      return;
    }
    
    // For cash or wallet, proceed directly
    await processPaymentAndCollectPremium();
  };
  
  const processPaymentAndCollectPremium = async () => {
    setPaymentProcessing(true);
    setShowPaymentDialog(false);
    setShowCardDialog(false);
    setStep('collect');
    
    try {
      let paymentTransactionId = null;
      
      // If card payment is selected, process card payment first via Traderoute
      if (selectedPaymentMethod === 'card') {
        toast.info('Processing card payment...');
        
        const cardPaymentResponse = await axios.post(
          `${API}/payment/card`,
          {
            card_number: cardDetails.cardNumber.replace(/\s/g, ''),
            expiry_date: cardDetails.expiryDate,
            cvv: cardDetails.cvv,
            card_type: cardDetails.cardType,
            amount: parseFloat(premiumAmount),
            description: `Insurance premium for ${policyData.policy_holder_name}`
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (cardPaymentResponse.data.status === 'success') {
          paymentTransactionId = cardPaymentResponse.data.transaction_id;
          toast.success('Card payment successful!');
        } else {
          throw new Error('Card payment failed');
        }
      }
      
      // Now collect the premium with payment method info
      const response = await axios.post(
        `${API}/insurance/collect-premium`,
        {
          lookup_type: lookupType,
          lookup_value: lookupValue,
          premium_amount: parseFloat(premiumAmount),
          policy_holder_name: policyData.policy_holder_name,
          receipt_delivery: deliveryMethod,
          delivery_address: deliveryAddress,
          payment_method: selectedPaymentMethod,
          card_transaction_id: paymentTransactionId // Include card transaction ID if card payment
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setReceiptData(response.data);
      setStep('receipt');
      toast.success(`Premium collected! Commission: R${response.data.commission}`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Collection failed');
      setStep('lookup');
      setShowPaymentDialog(false);
      setShowCardDialog(false);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleNewTransaction = () => {
    setStep('lookup');
    setLookupValue('');
    setPolicyData(null);
    setPremiumAmount('');
    setDeliveryAddress('');
    setReceiptData(null);
    setShowPaymentDialog(false);
    setSelectedPaymentMethod('cash');
    setPaymentProcessing(false);
  };

  return (
    <Card data-testid="insurance-premium-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <Shield className="inline mr-2" />Insurance Premium Collection
        </CardTitle>
        <CardDescription>Collect insurance premiums and earn R1.25 per transaction</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            Receipts can be sent via wallet, email, or WhatsApp
          </p>
        </div>

        {step === 'lookup' && (
          <form onSubmit={handleLookup} className="space-y-4">
            <div>
              <Label>Lookup Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="select-id-lookup-btn"
                  variant={lookupType === 'id_number' ? 'default' : 'outline'}
                  className={lookupType === 'id_number' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setLookupType('id_number')}
                >
                  ID Number
                </Button>
                <Button
                  type="button"
                  data-testid="select-policy-lookup-btn"
                  variant={lookupType === 'policy_number' ? 'default' : 'outline'}
                  className={lookupType === 'policy_number' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setLookupType('policy_number')}
                >
                  Policy Number
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="lookup_value">
                {lookupType === 'id_number' ? 'ID Number' : 'Policy Number'}
              </Label>
              <Input
                id="lookup_value"
                data-testid="lookup-value-input"
                placeholder={lookupType === 'id_number' ? '13 digits' : 'Enter policy number'}
                value={lookupValue}
                onChange={(e) => setLookupValue(e.target.value)}
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="lookup-policy-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Looking up...' : 'Lookup Policy'}
            </Button>
          </form>
        )}

        {step === 'collect' && policyData && (
          <div className="space-y-4">
            <div className="p-4 bg-[#FAF5F0] rounded-lg">
              <h3 className="font-semibold text-[#1B0C34] mb-2">Policy Information</h3>
              <div className="space-y-1 text-sm">
                <p><strong>Policy Holder:</strong> {policyData.policy_holder_name}</p>
                <p><strong>Policy Number:</strong> {policyData.policy_number}</p>
                <p><strong>Policy Type:</strong> {policyData.policy_type}</p>
                <p><strong>Provider:</strong> {policyData.insurance_provider}</p>
                <p><strong>Premium Due:</strong> R{policyData.premium_due.toFixed(2)}</p>
                <p><strong>Due Date:</strong> {policyData.due_date}</p>
                <p><strong>Payment Method:</strong> {selectedPaymentMethod.charAt(0).toUpperCase() + selectedPaymentMethod.slice(1)}</p>
              </div>
            </div>

            {paymentProcessing ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F2B10C] mx-auto"></div>
                <p className="mt-4 text-sm text-[#1B0C34]/70">Processing payment...</p>
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); /* Already processed */ }} className="space-y-4">
                <div>
                  <Label>Receipt Delivery Method</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      type="button"
                      data-testid="select-wallet-delivery-btn"
                      variant={deliveryMethod === 'wallet' ? 'default' : 'outline'}
                      className={deliveryMethod === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                      onClick={() => setDeliveryMethod('wallet')}
                    >
                      Wallet
                    </Button>
                    <Button
                      type="button"
                      data-testid="select-email-delivery-btn"
                      variant={deliveryMethod === 'email' ? 'default' : 'outline'}
                      className={deliveryMethod === 'email' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                      onClick={() => setDeliveryMethod('email')}
                    >
                      Email
                    </Button>
                    <Button
                      type="button"
                      data-testid="select-whatsapp-delivery-btn"
                      variant={deliveryMethod === 'whatsapp' ? 'default' : 'outline'}
                      className={deliveryMethod === 'whatsapp' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                      onClick={() => setDeliveryMethod('whatsapp')}
                    >
                      WhatsApp
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="delivery_address">
                    {deliveryMethod === 'wallet' ? 'Wallet ID' : deliveryMethod === 'email' ? 'Email Address' : 'Phone Number'}
                  </Label>
                  <Input
                    id="delivery_address"
                    data-testid="delivery-address-input"
                    placeholder={
                      deliveryMethod === 'wallet' ? 'MWXXXXXXXXXXXX' :
                      deliveryMethod === 'email' ? 'email@example.com' :
                      '+27XXXXXXXXX'
                    }
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep('lookup');
                      setShowPaymentDialog(false);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handlePaymentMethodSelected}
                    data-testid="confirm-collection-btn"
                    className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                    disabled={!deliveryAddress}
                  >
                    Confirm Collection
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}

        {step === 'receipt' && receiptData && (
          <div className="space-y-4" data-testid="receipt-view">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg">
              <div className="text-center mb-4">
                <CheckCircle className="w-16 h-16 mx-auto mb-2" />
                <h3 className="text-2xl font-bold">Payment Successful!</h3>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Receipt Number:</span>
                  <span className="font-mono font-bold">{receiptData.receipt_number}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Transaction ID:</span>
                  <span className="font-mono text-xs">{receiptData.transaction_id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Premium Amount:</span>
                  <span className="font-bold">R{receiptData.premium_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Your Commission:</span>
                  <span className="font-bold text-[#F2B10C]">R{receiptData.commission.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Total Credited:</span>
                  <span className="font-bold text-[#F2B10C]">R{receiptData.total_credited.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-white/10 rounded">
                <p className="text-xs font-semibold mb-1">Receipt Delivery</p>
                <p className="text-sm">{receiptData.delivery_status}</p>
              </div>
            </div>

            <Button
              onClick={handleNewTransaction}
              data-testid="new-transaction-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              New Transaction
            </Button>
          </div>
        )}
      </CardContent>

      {/* Payment Method Selection Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Payment Method</DialogTitle>
            <DialogDescription>
              Choose how the customer will pay the premium
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Premium Amount Display */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Premium Amount:</span>
                <span className="text-2xl font-bold text-blue-600">
                  R{policyData?.premium_due?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Your Wallet Balance:</span>
                <span className={`font-semibold ${
                  walletBalance < (policyData?.premium_due || 0)
                    ? 'text-red-600'
                    : 'text-green-600'
                }`}>
                  R{walletBalance.toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Insufficient Balance Warning for Cash */}
            {selectedPaymentMethod === 'cash' && walletBalance < (policyData?.premium_due || 0) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800 mb-1">Insufficient Wallet Balance</p>
                    <p className="text-sm text-red-700">
                      Cash collections require equivalent balance in your wallet. 
                      Required: R{policyData?.premium_due?.toFixed(2) || '0.00'}, 
                      Available: R{walletBalance.toFixed(2)}
                    </p>
                    <p className="text-sm text-red-700 mt-2">
                      Please top up your wallet or choose a different payment method.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Payment Method Selection */}
            <div className="space-y-3">
              <Label>Payment Method</Label>
              
              {/* Cash Option */}
              <div 
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPaymentMethod === 'cash' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                } ${
                  walletBalance < (policyData?.premium_due || 0)
                    ? 'opacity-50'
                    : ''
                }`}
                onClick={() => setSelectedPaymentMethod('cash')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'cash' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedPaymentMethod === 'cash' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">Cash</div>
                    <div className="text-sm text-gray-600">Collect physical cash from customer</div>
                    {walletBalance < (policyData?.premium_due || 0) && (
                      <div className="text-xs text-red-600 mt-1">⚠️ Insufficient wallet balance</div>
                    )}
                  </div>
                  <Banknote className="w-6 h-6 text-green-600" />
                </div>
              </div>
              
              {/* Card Option */}
              <div 
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPaymentMethod === 'card' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPaymentMethod('card')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'card' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedPaymentMethod === 'card' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">Card</div>
                    <div className="text-sm text-gray-600">Process card payment via Traderoute</div>
                  </div>
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              
              {/* Wallet Option */}
              <div 
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPaymentMethod === 'wallet' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPaymentMethod('wallet')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'wallet' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedPaymentMethod === 'wallet' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">Wallet</div>
                    <div className="text-sm text-gray-600">Pay from Mojo Money wallet</div>
                  </div>
                  <Wallet className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
            
            {/* Warning Message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Payment will be processed immediately upon confirmation.
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => {
                setShowPaymentDialog(false);
                setSelectedPaymentMethod('cash');
              }}
              disabled={paymentProcessing}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePaymentMethodSelected}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={
                paymentProcessing || 
                (selectedPaymentMethod === 'cash' && walletBalance < (policyData?.premium_due || 0))
              }
            >
              {paymentProcessing ? (
                <>Processing...</>
              ) : (
                selectedPaymentMethod === 'cash' && walletBalance < (policyData?.premium_due || 0) ? (
                  <>Insufficient Balance</>
                ) : (
                  <>✓ Select & Continue</>
                )
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Card Details Input Dialog */}
      <Dialog open={showCardDialog} onOpenChange={setShowCardDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Card Details</DialogTitle>
            <DialogDescription>
              Process card payment for R{premiumAmount} premium
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cardType">Card Type</Label>
              <select
                id="cardType"
                className="w-full p-2 border rounded-md"
                value={cardDetails.cardType}
                onChange={(e) => setCardDetails({...cardDetails, cardType: e.target.value})}
              >
                <option value="debit">Debit Card (2% fee)</option>
                <option value="credit">Credit Card (3.5% fee)</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="cardNumber">Card Number</Label>
              <Input
                id="cardNumber"
                type="text"
                placeholder="1234 5678 9012 3456"
                maxLength="19"
                value={cardDetails.cardNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s/g, '');
                  const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                  setCardDetails({...cardDetails, cardNumber: formatted});
                }}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  id="expiryDate"
                  type="text"
                  placeholder="MM/YY"
                  maxLength="5"
                  value={cardDetails.expiryDate}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length >= 2) {
                      value = value.slice(0, 2) + '/' + value.slice(2, 4);
                    }
                    setCardDetails({...cardDetails, expiryDate: value});
                  }}
                />
              </div>
              
              <div>
                <Label htmlFor="cvv">CVV</Label>
                <Input
                  id="cvv"
                  type="text"
                  placeholder="123"
                  maxLength="4"
                  value={cardDetails.cvv}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setCardDetails({...cardDetails, cvv: value});
                  }}
                />
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Amount to charge:</strong> R{premiumAmount}
                <br />
                <strong>Fee:</strong> {cardDetails.cardType === 'debit' ? '2%' : '3.5%'}
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => {
                setShowCardDialog(false);
                setShowPaymentDialog(true);
              }}
              disabled={paymentProcessing}
            >
              Back
            </Button>
            <Button 
              onClick={processPaymentAndCollectPremium}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={
                paymentProcessing || 
                !cardDetails.cardNumber || 
                !cardDetails.expiryDate || 
                !cardDetails.cvv ||
                cardDetails.cardNumber.replace(/\s/g, '').length < 13
              }
            >
              {paymentProcessing ? 'Processing...' : '✓ Process Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MerchantDashboard;
